import React, { useState } from "react";
import { Employee } from "../models";
import View360EmployeesList from "./employees-list/View360EmployeesList";
import { constants } from "shared/constants";
import styles from "./view360EmployeesTabContent.module.scss";
import { useInfiniteQuery } from "react-query";
import { GetEmployeesParameters } from "../service/contracts";
import EmployeeService from "../service/EmployeesService";
import pl from "shared/messages/pl";
import { Button, Divider, Flex, Input, SearchIcon, Text } from "@fluentui/react-northstar";
import NotesListShowMoreButton from "view360Card/features/notes/components/notes-list/show-more-button/NotesListShowMoreButton";

export interface View360EmployeesTabContentProps {
    intermediaryId: number | undefined;
}

const View360EmployeesTabContent: React.FC<View360EmployeesTabContentProps> = ({
    intermediaryId,
}) => {
    const isMobile = window.innerWidth < constants.view360ReportsMobileDeviceWidthPx;

    const [query, setQuery] = useState("");

    const { isFetching, isError, data, hasNextPage, fetchNextPage, refetch } = useInfiniteQuery(
        ["employees", intermediaryId],
        async ({ pageParam = 0 }) => {
            const pageSize = 200;

            const commonParams: GetEmployeesParameters = {
                id: intermediaryId!,
                offset: pageParam,
                limit: pageSize,
                phrase: query,
            };

            return await EmployeeService.getEmployeesList(commonParams);
        },
        {
            getNextPageParam: (_, pages) =>
                pages[pages.length - 1]?.hasMore
                    ? pages.flatMap((page) => page?.employees.length ?? 0).reduce((a, b) => a + b)
                    : undefined,
        }
    );

    const TextHeader = (text: string) => {
        return (
            <Flex hAlign="center" style={{ margin: "10px 0px" }}>
                <Text content={text} temporary />
            </Flex>
        );
    };

    const getFilterPanel = () => {
        return (
            <Flex gap="gap.small" hAlign={isMobile ? "center" : undefined} vAlign="center" >
                    <Input
                        disabled={isFetching}
                        value={query}
                        onChange={(e) => setQuery((e.currentTarget as EventTarget & HTMLInputElement).value)}
                        fluid
                        onKeyUp={async (event) => {
                            if (event?.keyCode === constants.enterKeyCode) {
                                await refetch();
                            }
                        }}
                        clearable
                    />
                    <Button
                        icon={<SearchIcon />}
                        primary
                        disabled={isFetching}
                        type="submit"
                        iconOnly
                        onClick={() => refetch()}
                    />
            </Flex>
        );
    };

    const Content = () => {
        if (isError) {
            return <span>{pl.view360Card.employeesList.errors.failedToFetchEmployees}</span>;
        }

        let employees = data?.pages.flatMap((page) => page?.employees ?? []);

        if (isFetching) {
            return TextHeader(pl.view360Card.notes.notesList.loading);
        }

        if (!employees || employees.length === 0) {
            return TextHeader(pl.view360Card.employeesList.employeesList.noEmployeesFound);
        }

        const employeesList: Employee[] = employees;

        return (
            <Flex column gap="gap.small" className={styles.container}>
                <View360EmployeesList isMobile={isMobile} employeesList={employeesList} />
                {hasNextPage && <NotesListShowMoreButton onClick={fetchNextPage} isLoading={isFetching} />}
            </Flex>
        )
    }

    return (
        <Flex column gap="gap.small">
            {getFilterPanel()}
            <Divider />
            <Content />
        </Flex>
    )
}

export default View360EmployeesTabContent;